import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchNakshatras, fetchCurrentMoon } from "@/lib/api";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const PLACEHOLDER = "https://images.pexels.com/photos/9665214/pexels-photo-9665214.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function NakshatraGallery() {
  const [nakshatras, setNakshatras] = useState([]);
  const [currentSlug, setCurrentSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const [naks, moon] = await Promise.all([
          fetchNakshatras(),
          fetchCurrentMoon().catch(() => null),
        ]);
        setNakshatras(naks);
        if (moon) setCurrentSlug(moon.nakshatra_slug);
      } catch (e) {
        console.error("Failed to load nakshatras:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto logo-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="gallery-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="gallery-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Dashboard</span>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="text-white/60 hover:text-white hover:bg-white/5 text-xs tracking-wider uppercase"
            onClick={() => navigate("/calculator")}
            data-testid="gallery-calc-btn"
          >
            Natal Calculator
          </Button>
        </div>
      </nav>

      <main className="pt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white/95 mb-3" data-testid="gallery-title">
            The 27 Nakshatras
          </h1>
          <p className="text-white/40 text-sm mb-12 max-w-xl">
            The lunar mansions of Vedic astrology. Each nakshatra spans 13°20' of the sidereal zodiac and carries its own mythology, deity, and psychological signature.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {nakshatras.map((nak, i) => {
            const imgUrl = nak.featured_image_url
              ? (nak.featured_image_url.startsWith("http") ? nak.featured_image_url : nak.featured_image_url)
              : PLACEHOLDER;
            const isActive = nak.slug === currentSlug;

            return (
              <motion.div
                key={nak.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.6) }}
              >
                <Link
                  to={`/nakshatras/${nak.slug}`}
                  className="block group"
                  data-testid={`gallery-card-${nak.slug}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden rounded-lg">
                    <img
                      src={imgUrl}
                      alt={nak.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    {/* Active badge */}
                    {isActive && (
                      <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.25)" }}>
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 moon-pulse" />
                        <span className="text-emerald-400 text-[10px] tracking-wider uppercase font-medium">Now</span>
                      </div>
                    )}

                    {/* Text overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase font-[Manrope] mb-1">
                        {nak.index}. {nak.planetary_ruler}
                      </p>
                      <p className="text-white/90 text-lg sm:text-xl font-[Cormorant_Garamond] font-light leading-tight group-hover:text-white transition-colors duration-200">
                        {nak.name}
                      </p>
                      <p className="text-white/30 text-xs mt-1 font-[Manrope] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Tap to explore
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
